import { Component, OnInit, HostListener } from '@angular/core';

import { Router, NavigationEnd } from '@angular/router';
import { filter, switchMap, take } from 'rxjs/operators';
import { Utils } from '@app-shared/utils';

import { SidebarService } from '@app-services/sidebar.service';
import { SessionService } from '@app-store/session/session.service';
import { Role, UserInfo } from '@app-store/session/session.store';
import { ProductService } from '@app-services/product.service';
import { FeatureGroupEnum } from '@app/enum/featureGroup.enum';
import { ClientService } from '@app-services/client.service';

@Component({
	selector: 'app-layout-restrict-area-sidebar',
	templateUrl: './layout-restrict-area-sidebar.component.html',
	styleUrls: ['./layout-restrict-area-sidebar.component.scss']
})
export class LayoutRestrictAreaSidebarComponent implements OnInit {
	isMobile = false;
	isAdmin = false;

	itemsMenu = [
		{
			type: 'link',
			label: 'menu.dashboard',
			link: '/admin/dashboard',
			icon: 'fal fa-chart-column',
			children: []
		},
		{
			type: 'dropdown',
			label: 'menu.personalization',
			icon: 'fal fa-cogs',
			roles: ['CLIENT', 'GROUP', 'CARDHOLDER'],
			children: [
				{
					type: 'link',
					label: 'menu.profile',
					link: '',
					exact: true,
					active: false
				},
				{
					type: 'link',
					label: 'menu.accountSettings',
					link: '',
					exact: true,
					active: false
				},
				{
					type: 'link',
					label: 'menu.promotedContents',
					link: '',
					active: false
				}
			]
		},
		{
			type: 'link',
			label: 'menu.clients',
			link: '/admin/client',
			icon: 'fal fa-user-group',
			dropdown: false,
			children: [],
			roles: ['ADMIN', 'PRODUCT_ADMIN']
		},
		{
			type: 'link',
			label: 'menu.groups',
			link: '/admin/group',
			icon: 'fal fa-layer-group',
			dropdown: false,
			children: [],
			roles: ['ADMIN', 'PRODUCT_ADMIN', 'CLIENT']
		},
		{
			type: 'link',
			label: 'menu.cardholders',
			link: '/admin/cardholder',
			icon: 'fal fa-id-card',
			dropdown: false,
			children: [],
			roles: ['ADMIN', 'PRODUCT_ADMIN', 'CLIENT', 'GROUP']
		},
		{
			type: 'link',
			label: 'menu.showcase',
			link: '/admin/showcase',
			icon: 'fal fa-star',
			roles: []
		}
	];

	menuBilling = {
		type: 'link',
		label: 'menu.billing',
		link: '/admin/billing',
		icon: 'fal fa-credit-card',
		dropdown: false,
		roles: [Role.CLIENT],
		children: []
	};

	featureMenuMap = [
		{
			feature: FeatureGroupEnum.REAL_ESTATE,
			item: {
				type: 'link',
				label: 'menu.leads',
				link: '/admin/leads',
				icon: 'fal fa-user-plus',
				dropdown: false,
				children: [],
				roles: []
			}
		},
		{
			feature: FeatureGroupEnum.MARKET_PRICING_ASSISTANT,
			item: {
				type: 'link',
				label: 'menu.search-properties',
				link: '/admin/search-properties',
				icon: 'fal fa-magnifying-glass',
				dropdown: false,
				children: [],
				roles: []
			}
		},
		{
			feature: FeatureGroupEnum.REFER_A_FRIEND,
			item: {
				type: 'link',
				label: 'menu.refer-and-earn',
				link: '/admin/refer-and-earn',
				icon: 'fal fa-share',
				dropdown: false,
				roles: [Role.CLIENT],
				children: []
			}
		}
	];

	requiredPaidPlanRoutes = ['/admin/refer-and-earn'];

	user: UserInfo;

	constructor(
		public router: Router,
		public sidebarService: SidebarService,
		private readonly sessionService: SessionService,
		private readonly productService: ProductService,
		private readonly clientService: ClientService
	) {}

	ngOnInit() {
		this.sessionService.getUser().subscribe(u => {
			this.user = u;
			this.initAdditionalMenuItems();

			if (this.user.hasAnyRole('CLIENT', 'GROUP', 'CARDHOLDER')) {
				//define personalization and promotedContents link according to roles
				let personalizationLink: string;
				let promotedContentsLink: string;
				if (this.user.hasRole('CLIENT')) {
					personalizationLink = '/admin/personalization/' + this.user.clientId;
					promotedContentsLink = '/admin/promoted-content/client';
				} else if (this.user.hasRole('GROUP')) {
					personalizationLink = '/admin/group/' + this.user.groupId;
					promotedContentsLink = '/admin/promoted-content/group';
				} else if (this.user.hasRole('CARDHOLDER')) {
					personalizationLink = '/admin/cardholder/' + this.user.cardholderId;
					promotedContentsLink = '/admin/promoted-content/cardholder';
				}

				const personalizationMenuItem = this.itemsMenu.filter(i => i.label === 'menu.personalization')[0];
				personalizationMenuItem.children.filter(i => i.label === 'menu.profile')[0].link = personalizationLink;
				personalizationMenuItem.children.filter(i => i.label === 'menu.accountSettings')[0].link =
					personalizationLink + '/account-settings';
				personalizationMenuItem.children.filter(i => i.label === 'menu.promotedContents')[0].link =
					promotedContentsLink;

				//remove groups e cardholders menu items if the user is an individual client
				if (!this.user.hasAnyRoleArr(['ADMIN', 'PRODUCT_ADMINN']) && this.user.individualClient) {
					this.itemsMenu = this.itemsMenu.filter(i => i.label !== 'menu.groups' && i.label !== 'menu.cardholders');
				}
			}
		});

		this.updateSidebar();
		this.updateActiveMenu();
		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
			if (Utils.isMobile()) {
				this.sidebarService.active = false;
			}
			this.updateActiveMenu();
		});
	}

	initAdditionalMenuItems() {
		if (!this.featureMenuMap || this.featureMenuMap.length === 0) return;

		this.clientService
			.currentPlanIsPaid(this.user.clientId)
			.pipe(
				take(1),
				switchMap(isPaid => {
					if (!isPaid) {
						this.featureMenuMap = this.featureMenuMap.filter(m => !this.requiredPaidPlanRoutes.includes(m.item.link));
					}

					return this.productService.getProductInfo().pipe(take(1));
				})
			)
			.subscribe(productInfo => {
				const additional = this.featureMenuMap
					.filter(m => productInfo.hasFeature(m.feature))
					.map(m => m.item)
					.filter(
						newItem =>
							!this.itemsMenu.some(existing => existing.label === newItem.label && existing.link === newItem.link)
					);
				if (productInfo.plansEnabled) additional.push(this.menuBilling);
				if (additional.length) {
					this.itemsMenu = [...this.itemsMenu, ...additional];
					this.updateActiveMenu();
				}
			});
	}

	updateSidebar() {
		this.sidebarService.active = !Utils.isMobile();
		this.isMobile = Utils.isMobile();
	}

	@HostListener('window:resize', ['$event'])
	onResize(_event: Event) {
		this.updateSidebar();
		const isDesktop = window && window.matchMedia('(max-width: 1200px)').matches;
		if (this.isMobile) {
			this.sidebarService.minSidebar = false;
			return;
		}

		if (isDesktop && !this.sidebarService.minSidebar) {
			this.sidebarService.minSidebar = true;
			return;
		}
		if (!isDesktop && !!this.sidebarService.minSidebar) {
			this.sidebarService.minSidebar = false;
			return;
		}
	}

	setActiveMenu(item) {
		setTimeout(() => {
			item.dropdown = !item.dropdown;
		}, 0);
	}

	hideMenu(): boolean {
		return !this.user || this.user.hasPendingOnboarding;
	}

	private updateActiveMenu() {
		const activeRoute = this.router.url;
		this.itemsMenu.forEach(item => {
			item.dropdown = false;
			if (activeRoute.indexOf(item.link) !== -1) {
				item.dropdown = true;
			}
			if (item.children) {
				item.children.forEach(subItem => {
					subItem.active = false;
					if (activeRoute.indexOf(subItem.link) !== -1) {
						item.dropdown = true;
					}
				});
			}
		});
	}

	toggleSidebar() {
		this.sidebarService.minSidebar = !this.sidebarService.minSidebar;
	}
}
